package org.dronda.app.web.ui.eventdetail

import androidx.compose.runtime.Stable
import com.github.michaelbull.result.onErr
import com.github.michaelbull.result.onOk
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import org.dronda.api.wishlist.EventLimits
import org.dronda.app.web.repository.EventRepository
import org.dronda.app.web.ui.util.formatMedium
import org.dronda.service.wishlist.core.domain.EventDetails
import org.dronda.service.wishlist.core.domain.EventMode

class EventDetailViewModel(
    private val eventId: String,
    private val eventRepository: EventRepository
) {
    private val scope = CoroutineScope(Dispatchers.Default + SupervisorJob())
    private val _uiState = MutableStateFlow<UiState>(UiState.Loading)
    val uiState: StateFlow<UiState> = _uiState.asStateFlow()

    init {
        loadEvent()
    }

    private fun loadEvent() {
        scope.launch {
            _uiState.value = UiState.Loading
            eventRepository.getEvent(eventId)
                .onOk { event ->
                    _uiState.value = UiState.Success(event.toDisplay())
                }
                .onErr {
                    _uiState.value = UiState.Error("Failed to load event")
                }
        }
    }

    @Stable
    sealed interface UiState {
        data object Loading : UiState
        data class Success(val event: EventDisplay) : UiState
        data class Error(val message: String) : UiState
    }

    data class EventDisplay(
        val title: String,
        val subtitle: String,
        val modeBadge: String?,
        val isAdmin: Boolean,
        val santaBanner: SantaBanner?,
        val wishlists: List<WishlistCardDisplay>,
    )

    data class SantaBanner(val title: String, val body: String?, val wishlistId: String?)

    data class WishlistCardDisplay(val id: String, val title: String, val subtitle: String)
}

internal fun EventDetails.toDisplay(): EventDetailViewModel.EventDisplay {
    val santaMode = event.mode == EventMode.SECRET_SANTA
    val recipientWishlist = santa?.recipientId?.let { recipientId -> wishlists.firstOrNull { it.owner.id == recipientId } }

    return EventDetailViewModel.EventDisplay(
        title = event.name,
        subtitle = buildList {
            add(event.date.formatMedium())
            event.budget?.takeIf { santaMode }?.let { add("Budget $it") }
            add("${wishlists.size} / ${EventLimits.MAX_PARTICIPANTS} people")
        }.joinToString(" · "),
        modeBadge = if (santaMode) "🎅 Secret Santa" else null,
        isAdmin = isAdmin,
        santaBanner = when {
            !santaMode -> null
            recipientWishlist != null -> EventDetailViewModel.SantaBanner(
                title = "🎁 You're ${recipientWishlist.owner.name}'s Secret Santa!",
                body = "Keep it secret. Tap to see their wishlist.",
                wishlistId = recipientWishlist.id.toString(),
            )
            santa?.isDrawn == true -> EventDetailViewModel.SantaBanner(
                title = "You joined after names were drawn",
                body = "The organiser will add you to the draw soon.",
                wishlistId = null,
            )
            else -> EventDetailViewModel.SantaBanner(
                title = "Names haven't been drawn yet",
                body = "You'll get an email with your person once the organiser draws names.",
                wishlistId = null,
            )
        },
        wishlists = wishlists.map { wishlist ->
            EventDetailViewModel.WishlistCardDisplay(
                id = wishlist.id.toString(),
                title = "${wishlist.owner.name}'s Wishlist",
                subtitle = wishlist.name,
            )
        },
    )
}
